<template>
  <v-container class="pa-0">
    <v-row>
      <v-col cols="12">
        <v-card outlined class="modern-card overflow-hidden shadow-sm">
          <v-data-table
            :headers="headers"
            :items="items"
            :search="search"
            sort-by="date"
            :sort-desc="true"
            :items-per-page="10"
            :loading="loading"
            class="modern-table"
            :footer-props="{
              showFirstLastPage: true,
              itemsPerPageOptions: [10, 20, 50, 100],
            }"
          >
            <!-- Integrated Top Bar -->
            <template v-slot:top>
              <v-sheet color="bg-lightgrey" class="px-6 py-4 d-flex align-center border-bottom bg-lightgrey">
                <div class="d-flex align-center">
                  <v-avatar size="32" color="primary lighten-5" class="mr-3">
                    <v-icon color="primary" small>fas fa-list-check</v-icon>
                  </v-avatar>
                  <span class="caption font-weight-black uppercase tracking-widest grey--text">
                    Desglose de Órdenes ({{ items.length }})
                  </span>
                </div>
                <v-spacer></v-spacer>
                <div class="d-flex align-center" style="gap: 16px;">
                  <v-text-field
                    v-model="search"
                    placeholder="Buscar en detalle..."
                    prepend-inner-icon="fas fa-search"
                    outlined dense hide-details clearable
                    :disabled="loading"
                    class="modern-search-field"
                    style="max-width: 300px;"
                  ></v-text-field>
                  <export-product-sales-detail 
                    :product="items"
                    :filter="filter" 
                    :options="options" 
                    :disabled="loading"
                  />
                </div>
              </v-sheet>
            </template>

            <!-- OT Column with Link-style or Chip -->
            <template v-slot:item.work_order="{ item }">
              <div class="d-flex align-center">
                <v-chip x-small label color="primary" dark class="font-weight-black mr-2">
                  OT #{{ item.work_order }}
                </v-chip>
              </div>
            </template>

            <!-- CN Column -->
            <template v-slot:item.sales_note="{ item }">
              <span class="caption font-weight-black secondary--text">CN #{{ item.sales_note }}</span>
            </template>

            <!-- Project Name -->
            <template v-slot:item.work_order_name="{ item }">
              <div class="body-2 font-weight-bold grey--text text--darken-3 lh-1-2 py-2">
                {{ item.work_order_name }}
              </div>
            </template>

            <!-- Customer -->
            <template v-slot:item.customer="{ item }">
              <div class="caption font-weight-medium grey--text">{{ item.customer }}</div>
            </template>

            <!-- Date -->
            <template v-slot:item.date="{ item }">
              <span class="caption grey--text font-weight-bold">{{ item.date | moment("DD/MM/YYYY") }}</span>
            </template>

            <!-- Quantity & Mts2 -->
            <template v-slot:item.qty="{ item }">
              <span class="body-2 font-weight-black">{{ item.qty | number('0,0') }}</span>
            </template>

            <template v-slot:item.mts2="{ item }">
              <span class="body-2 font-weight-medium">{{ item.mts2 | number('0,0.00') }}</span>
              <span class="caption grey--text ml-1 text-lowercase">m²</span>
            </template>

            <!-- Total Sales -->
            <template v-slot:item.total_sales_price="{ item }">
              <span class="body-2 font-weight-black secondary--text">{{ item.total_sales_price | moneda }}</span>
            </template>

          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { fetchProductSalesDetail } from '@/api/reports'
import ExportProductSalesDetail from '@/components/reports/ExportProductSalesDetail'
import notifications from '@/utils/notifications'
import moment from 'moment'

export default {
  name: 'ProductSalesReportDetail',
  props: ['sku', 'filter', 'product'],
  components: { ExportProductSalesDetail },
  data() {
    return {
      options: {},
      items: [],
      totalItems: 0,
      search: '',
      loading: false,
      headers: [
        { text: 'Fecha', value: 'date', class: 'table-header', width: '100px' },
        { text: 'OT', value: 'work_order', class: 'table-header', align: 'start', width: '100px' },
        { text: 'Cot.', value: 'sales_note', class: 'table-header', align: 'start', width: '100px' },
        { text: 'Proyecto', value: 'work_order_name', class: 'table-header' },
        { text: 'Cliente', value: 'customer', class: 'table-header' },
        { text: 'Vendedor', value: 'seller', class: 'table-header', width: '150px' },
        { text: 'Cant.', value: 'qty', class: 'table-header', align: 'end', width: '80px' },
        { text: 'Mts²', value: 'mts2', class: 'table-header', align: 'end', width: '100px' },
        { text: 'Total Venta', value: 'total_sales_price', class: 'table-header', align: 'end', width: '130px' }
      ]
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      try {
        let created_after = ''
        let created_before = ''
        if (this.filter.created_after) created_after = moment(this.filter.created_after).utc().format('YYYY-MM-DD HH:mm')
        if (this.filter.created_before) created_before = moment(this.filter.created_before).utc().endOf('day').format('YYYY-MM-DD HH:mm')
        const query = new URLSearchParams({ sku: this.sku, created_after, created_before }).toString()
        this.items = await fetchProductSalesDetail(query)
      } catch (e) {
        notifications.showError(e)
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    sku: {
      handler() {
        this.fetch()
      },
      immediate: true
    },
  },
  mounted() {
    this.fetch()
  }
}
</script>

<style scoped>
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }

/* Table Styling */
.modern-table >>> thead th {
  background-color: #f8fbfc !important;
  height: 48px !important;
  text-transform: uppercase;
  font-size: 0.7rem !important;
  letter-spacing: 1px;
  font-weight: 900 !important;
  color: #607d8b !important;
}

.modern-table >>> td {
  height: 52px !important;
  border-bottom: 1px solid #f0f3f5 !important;
}

.modern-search-field >>> .v-input__control >>> .v-input__slot {
  background-color: white !important;
  border-radius: 10px !important;
}

.uppercase { text-transform: uppercase; letter-spacing: 0.8px; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -0.5px; }
.lh-1-2 { line-height: 1.2 !important; }
.opacity-40 { opacity: 0.4; }
</style>
